import type { en } from "./en";

// French translations. Missing keys fall back to English via i18next.
export const fr: typeof en = {
  common: {
    home: "Accueil",
    no_scores: "Pas encore de points",
    points_count_one: "{{count}} point",
    points_count_other: "{{count}} points",
    questions_count_one: "{{count}} question",
    questions_count_other: "{{count}} questions",
  },
  home: {
    placeholder: "Code de la partie",
    enter: "Rejoindre",
    error_invalid_code:
      "Saisis le code à 6 caractères que t'a donné ton enseignant.",
    footer: "Lance un Binogi Buzz depuis les produits Binogi.com.",
    language_label: "Langue",
  },
  errors: {
    generic: "Un problème est survenu. Réessaie.",
    stale_client: "Ta page n’est plus à jour — recharge-la.",
    session_not_found: "Partie introuvable.",
    session_not_open: "Cette partie est terminée.",
    session_not_active: "Cette question ou cette partie est déjà terminée.",
    not_teacher: "Seul l’enseignant peut faire ça.",
    already_answered: "Tu as déjà répondu à cette question.",
    wrong_question_index: "Cette question n’a pas encore commencé.",
    name_taken: "Ce nom est déjà pris. Essaie-en un autre.",
    character_busy: "Impossible de t’attribuer un personnage pour l’instant — réessaie.",
  },
  host: {
    input: {
      heading: "Lancer un Buzz",
      lead:
        "Saisis le code de la leçon Binogi que tes élèves étudient. On charge les questions et on crée une partie à projeter au tableau.",
      lesson_code_label: "Code de la leçon",
      placeholder_code: "BIH750",
      load_button: "Charger la leçon",
      try_hint: "Essaie :",
      error_invalid_code: "Saisis un code de leçon Binogi, ex. BIH750.",
      error_not_found: "Aucune leçon trouvée pour le code {{code}}.",
      error_unreachable: "Impossible de joindre le serveur des leçons. Vérifie ta connexion et réessaie.",
      error_no_questions: "La leçon {{code}} n’a aucune question utilisable par Buzz.",
      error_no_level_questions: "La leçon {{code}} n’a aucune question du niveau choisi.",
    },
    loading: "Chargement de la leçon…",
    placeholder_title: "Lancer un Binogi Buzz",
    tab_details: "Détails",
    tab_questions: "Questions",
    settings: "Paramètres",
    number_of_students: "Nombre d'élèves",
    fewer_students: "Moins d’élèves",
    more_students: "Plus d’élèves",
    player_names: "Noms des joueurs",
    player_anonymous: "Anonyme",
    player_self_chosen: "Les élèves choisissent",
    language: "Langue",
    language_teacher: "L'enseignant choisit",
    language_students: "Les élèves choisissent",
    time_per_question: "Temps par question",
    time_option: "{{seconds}} s",
    start_button: "Démarrer la partie",
    start_disabled: "Indique la taille de la classe pour démarrer",
    creating: "Création de la partie…",
    error_no_questions: "Garde au moins une question avant de démarrer.",
    error_create_failed: "Impossible de créer la partie",
    error_load_failed: "Impossible de charger la leçon",
    no_session: "Aucune partie en cours.",
    back_to_review: "Retour aux questions",
    errors: {
      dismiss: "Fermer",
      refresh: "Recharger",
      attach_failed: "Cet écran n’a pas pu se connecter en tant qu’enseignant.",
      unreachable: "Impossible de joindre le serveur. Recharge la page pour réessayer.",
      no_token: "Cet onglet ne peut plus piloter le quiz. Ouvre Buzz depuis ta leçon pour lancer une nouvelle partie.",
      taken_over: "Ce quiz est maintenant piloté depuis un autre écran.",
      stale_question: "Buzz a été mis à jour pendant ce quiz, cet écran ne peut donc plus le poursuivre. Recharge la page pour reprendre le quiz là où en est la classe.",
      question_moved_on: "Le quiz est déjà passé à la suite, cet écran a donc pris du retard. Recharge la page pour reprendre le quiz là où en est la classe.",
    },
    entry: {
      opening: "Ouverture de la vue enseignant…",
      token_title: "Impossible d’ouvrir ce lien enseignant",
      token_body: "Il manque la clé enseignant dans le lien, ou il a expiré. Ouvre Buzz depuis ta leçon pour créer une nouvelle partie.",
      not_found_title: "Partie introuvable",
      not_found_body: "Cette partie de quiz n’existe plus. Crée-en une nouvelle pour continuer.",
      back: "Retour à l’accueil",
    },
    questions: {
      drag_aria: "Glisser pour réordonner",
      remove_aria: "Retirer la question",
      empty: "Plus aucune question dans cette partie.",
      add: "Ajouter",
      add_aria: "Ajouter la question: {{text}}",
      add_all: "Tout ajouter",
      add_all_n: "Ajouter les {{count}} questions",
      more_one: "{{count}} question de plus dans cette leçon",
      more_other: "{{count}} questions de plus dans cette leçon",
      level_easy: "Facile",
      level_medium: "Moyen",
      level_hard: "Difficile",
      multi: "Multiple",
    },
    lobby: {
      go_to: "Va sur <strong>buzz.binogi.com</strong> et saisis :",
      or_qr: "…ou scanne le QR code",
      students_joined_of: "{{joined}} sur {{expected}} élèves connectés",
      students_joined_one: "{{count}} élève connecté",
      students_joined_other: "{{count}} élèves connectés",
      waiting: "En attente des élèves…",
      start_game: "Lancer la partie",
      connected: "Connecté",
      connecting: "Connexion…",
      copy_link: "Copier le lien",
      copy_link_copied: "Copié !",
    },
    question: {
      header: "Question {{index}} sur {{total}}",
      level: "Niveau {{level}}",
      none: "Aucune question en cours.",
      answered: "Répondu",
      top_n: "Top {{count}}",
      answers_heading: "Réponses",
      end_question: "Terminer la question",
      next_question: "Question suivante",
      see_results: "Voir les résultats finaux",
    },
    results: {
      heading: "Résultats finaux",
      all_players: "Tous les joueurs",
      end_session: "Terminer la partie",
      new_session: "Nouvelle partie",
      none: "Aucun joueur dans cette partie.",
    },
  },
  join: {
    code_label: "Code de la partie",
    name_label: "Ton prénom",
    name_placeholder: "Écris ton prénom",
    join_button: "Rejoindre",
    joining: "Connexion…",
    checking: "Vérification…",
    character_hint:
      "Tu recevras un nom de personnage Binogi amusant en rejoignant.",
    error_not_found:
      "Ce code ne correspond pas encore à une partie en direct. Vérifie avec ton enseignant.",
    error_lookup_failed: "Impossible de trouver la partie",
    error_code_required: "Saisis le code à 6 caractères.",
    error_name_required: "Écris ton prénom.",
  },
  play: {
    superseded: "Tu es connecté dans un autre onglet — ferme-le pour continuer à jouer ici.",
    back_to_join: "Retour pour rejoindre",
    no_round: "Aucune donnée de manche.",
    not_joined: "Tu n’as rejoint aucune partie.",
    no_question: "Aucune question en cours.",
    keep_awake_hint: "Touche l’écran pour qu’il reste allumé",
    waiting: {
      you_are: "Tu es",
      get_ready_named: "Prépare-toi, {{name}} !",
      get_ready: "Prépare-toi pour le quiz !",
      in_room_label: "Élèves dans la salle",
      in_room_one: "{{count}} élève dans la salle",
      in_room_other: "{{count}} élèves dans la salle",
    },
    question: {
      progress: "{{index}} / {{total}}",
      answered: "Répondu !",
      times_up: "Temps écoulé",
      submit: "Valider",
      submit_n: "Valider {{count}} réponses",
    },
    between: {
      correct: "Bonne réponse !",
      wrong: "Presque !",
      times_up: "Temps écoulé",
      points_gained: "+{{count}} points",
      your_score: "Ton score",
      your_rank: "Position {{rank}}",
      top_n: "Top {{count}}",
      waiting_next: "En attente de la prochaine question…",
    },
    finished: {
      heading: "Merci d'avoir joué à Binogi Buzz !",
      your_final_rank: "Ton classement final",
      top_n: "Top {{count}}",
      none: "Aucun résultat à afficher.",
    },
  },
  characters: {
    heading: "Personnages Binogi",
    lead:
      "{{count}} élèves reçoivent un personnage en rejoignant, dans cet ordre.",
  },
};
